import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
} from '@material-ui/core'
import TagIcon from '@material-ui/icons/LocalOffer'
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer'
import { GetStaticPaths, GetStaticProps } from 'next'
import React from 'react'

import ButtonLink from '@aletheia/common/components/ButtonLink'
import Page from '@aletheia/common/components/Page'

import {
  fetchFaqs,
  fetchSeries,
  fetchTag,
  IFaq,
  isEntry,
  isFaq,
  isTag,
  ITag,
} from '../../../contentful'

const useStyles = makeStyles((theme) => ({
  questionListItem: {
    '& .MuiTypography-root': {
      fontFamily: theme.typography.h1.fontFamily,
    },
  },
}))

type FAQTagPageProps = {
  Tag: ITag
  FAQs: IFaq[]
}

const FAQTagPage: React.FC<FAQTagPageProps> = ({ Tag, FAQs }) => {
  const classes = useStyles()
  return (
    <Page
      title={`Tag: ${Tag.fields.title}`}
      pageTitle={
        <>
          <TagIcon /> {Tag.fields.title}
        </>
      }
      breadcrumbsBase={{ href: '/help', label: 'Help Center' }}
    >
      <List>
        {FAQs.map((FAQ) => (
          <ListItem
            key={FAQ.sys.id}
            button
            component={ButtonLink}
            href={`/help/[id]`}
            as={`/help/${FAQ.sys.id}`}
            className={classes.questionListItem}
          >
            <ListItemIcon>
              <QuestionAnswerIcon />
            </ListItemIcon>
            <ListItemText>{FAQ.fields.title}</ListItemText>
          </ListItem>
        ))}
      </List>
    </Page>
  )
}

export const getStaticProps: GetStaticProps<
  FAQTagPageProps,
  { slug: string }
> = async ({ params }) => {
  if (!params?.slug) throw new Error('No slug provided')
  const Tag = await fetchTag(params.slug)
  if (!Tag) throw new Error('Tag not found')
  const FAQs = await fetchFaqs({
    links_to_entry: Tag.sys.id,
    order: '-sys.updatedAt',
  })
  return {
    props: { Tag, FAQs },
    revalidate: 20,
  }
}

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  const FAQSeries = await fetchSeries('faq-entries')
  if (!FAQSeries) throw new Error('Could not find series `faq-entries`')
  const FAQs = FAQSeries.fields.entries.filter(isEntry).filter(isFaq)
  const tagSlugs: string[] = []
  // get all unique tag slugs
  for (const FAQ of FAQs) {
    const Tags = FAQ.fields.tags?.filter(isEntry).filter(isTag)
    if (!Tags) continue
    for (const Tag of Tags) {
      const { slug } = Tag.fields
      if (!tagSlugs.includes(slug)) tagSlugs.push(slug)
    }
  }

  return {
    paths: tagSlugs.map((slug) => ({ params: { slug } })),
    fallback: false,
  }
}

export default FAQTagPage
